import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../Services/AuthService';
import SubscriptionQuotaCard, { SubscriptionQuotaCardHandle } from '../../Components/Architect/SubscriptionQuotaCard';
import { filterProjectsByName } from '../../utils/projectFilter';

// Interfaces para proyectos en curso
interface ProjectInProgress {
    projectId: string;
    internalName: string;
    externalName: string;
    interventionType: 'NEW_CONSTRUCTION' | 'RENOVATION' | string;
    surface: string;
    clientEmail: string;
    clientSize: 'SINGULAR' | 'PLURAL' | string;
    clientTone: 'INFORMAL' | 'FORMAL' | string;
    ownerId: string;
    clientId: string | null;
    createdAt: string;
    updatedAt: string;
    completedSections: number;
    totalSections: number;
}

interface ProjectFormData {
    // Step 1
    internalName: string;
    title: string;
    interventionType: 'NEW_CONSTRUCTION' | 'RENOVATION' | '';
    surfaceArea: string;
    // Step 2
    clientEmail: string;
    clientSize: 'SINGULAR' | 'PLURAL' | '';
}

const ArchitectDashboard: React.FC = () => {
    const [projects, setProjects] = useState<ProjectInProgress[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isProjectFormOpen, setIsProjectFormOpen] = useState<boolean>(false);
    const quotaCardRef = useRef<SubscriptionQuotaCardHandle>(null);
    const [formStep, setFormStep] = useState<1 | 2>(1);
    const [formError, setFormError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [projectFormData, setProjectFormData] = useState<ProjectFormData>({
        internalName: '',
        title: '',
        interventionType: '',
        surfaceArea: '',
        clientEmail: '',
        clientSize: ''
    });

    // Cargar proyectos en curso
    useEffect(() => {
        fetchProjects();
    }, []);

    // Función para cargar proyectos en curso
    const fetchProjects = async () => {
        setIsLoading(true);

        try {
            const response = await apiClient.get('/quiz/projects');
            const projectsWithDefaults = response.data.map((project: any) => ({
                ...project,
                completedSections: 0,
                totalSections: 10
            }));
            setProjects(projectsWithDefaults);
        } catch (err) {
            console.error('Error al cargar proyectos:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Form handlers
    const toggleProjectForm = () => {
        setIsProjectFormOpen(!isProjectFormOpen);
        if (!isProjectFormOpen) {
            // Reset form when opening
            setProjectFormData({
                internalName: '',
                title: '',
                interventionType: '',
                surfaceArea: '',
                clientEmail: '',
                clientSize: ''
            });
            setFormStep(1);
            setFormError('');
        } else {
            // Clear error when closing
            setFormError('');
        }
    };

    const handleInputChange = (field: keyof ProjectFormData, value: string) => {
        setProjectFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing/selecting
        if (formError) {
            setFormError('');
        }
    };

    const handleNextStep = () => {
        // Validate step 1
        if (!projectFormData.internalName) {
            setFormError('Por favor, completa todos los campos requeridos');
            return;
        }

        if (!projectFormData.title) {
            setFormError('Por favor, completa todos los campos requeridos');
            return;
        }

        if (!projectFormData.interventionType) {
            setFormError('Por favor, selecciona un tipo de intervención');
            return;
        }
        if (!projectFormData.surfaceArea) {
            setFormError('Por favor, selecciona un rango de superficie');
            return;
        }

        setFormError('');
        setFormStep(2);
    };

    const handlePreviousStep = () => {
        setFormError('');
        setFormStep(1);
    };

    const handleSubmitProject = async (e: React.FormEvent) => {
        console.log('Submitting project form:', projectFormData);

        e.preventDefault();
        setFormError('');

        // Validate step 2
        if (!projectFormData.clientEmail) {
            setFormError('Por favor, completa todos los campos requeridos');
            return;
        }

         if (!projectFormData.clientSize) {
            setFormError('Por favor, completa todos los campos requeridos');
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                internal_name: projectFormData.internalName,
                external_name: projectFormData.title,
                intervention_type: projectFormData.interventionType,
                surface: projectFormData.surfaceArea,
                client_email: projectFormData.clientEmail,
                client_size: projectFormData.clientSize,
            };

            await apiClient.post('/quiz/project', payload);

            toggleProjectForm();
            fetchProjects();
            quotaCardRef.current?.refresh();
        } catch (error: any) {
            console.error('Error creating project:', error);

            let errorMessage = 'Error al crear el proyecto';

            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data.error === 'string') {
                    errorMessage = data.error;
                } else if (typeof data.error === 'object' && data.error.message) {
                    errorMessage = data.error.message;
                } else if (typeof data.message === 'string') {
                    errorMessage = data.message;
                } else if (typeof data.message === 'object' && data.message.message) {
                    errorMessage = data.message.message;
                } else if (typeof data.detail === 'string') {
                    errorMessage = data.detail;
                } else if (typeof data.detail === 'object' && data.detail.message) {
                    errorMessage = data.detail.message;
                }
            }

            setFormError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Formatear fecha
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(date);
        } catch (e) {
            return 'Fecha inválida';
        }
    };

    // Función para obtener el texto del tipo de proyecto
    const getProjectTypeText = (type: string): string => {
        switch (type) {
            case 'NEW_CONSTRUCTION':
                return 'Obra Nueva';
            case 'RENOVATION':
                return 'Reforma';
            default:
                return type || 'Desconocido';
        }
    };

    // Función para obtener el texto de la superficie
    const getSurfaceText = (surface: string): string => {
        switch (surface) {
            case 'AREA_LOWER_THAN_50':
                return '< 50m²';
            case 'AREA_BETWEEN_51_AND_100':
                return '51 - 100m²';
            case 'AREA_BETWEEN_101_AND_200':
                return '101 - 200m²';
            case 'AREA_BETWEEN_201_AND_500':
                return '201 - 500m²';
            case 'AREA_GREATER_THAN_500':
                return '> 500m²';
            default:
                return surface || 'Desconocido';
        }
    };

    // Filtro por nombre de proyecto (título externo, nombre interno o email del cliente)
    const filteredProjects = filterProjectsByName(projects, searchTerm);

    return (
        <div className="container-custom">
            <div className="app-main">
                <SubscriptionQuotaCard ref={quotaCardRef} />

                {/* Buscador por nombre de proyecto (debajo del contador de suscripción) */}
                {!isLoading && projects.length > 0 && (
                    <div className="input-group mb-3">
                        <span className="input-group-text bg-white">
                            <i className="bi bi-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar proyecto por nombre..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Buscar proyecto por nombre"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setSearchTerm('')}
                                aria-label="Limpiar búsqueda"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        )}
                    </div>
                )}

                {/* Projects in progress displayed as quiz groups */}
                <div className="d-flex flex-column gap-3">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border spinner-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className="mt-3">Cargando proyectos...</p>
                        </div>
                    ) : projects.length > 0 && filteredProjects.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            No se encontraron proyectos que coincidan con "{searchTerm}".
                        </div>
                    ) : projects.length > 0 ? (
                        filteredProjects.map(project => (
                            <Link
                                key={project.projectId}
                                to={`/architect/project/${project.projectId}`}
                                className="text-decoration-none"
                                style={{ color: 'inherit' }}
                            >
                                <div className="quiz-group-card-full position-relative" style={{ cursor: 'pointer' }}>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center gap-3 mb-2">
                                                <div className="quiz-group-title">
                                                    {project.externalName}
                                                </div>
                                            </div>
                                            <div className="quiz-group-subtitle mb-2">
                                                {getProjectTypeText(project.interventionType)}&nbsp;&nbsp;|&nbsp;&nbsp;{getSurfaceText(project.surface)}&nbsp;&nbsp;|&nbsp;&nbsp;Inicio: {formatDate(project.createdAt)}&nbsp;&nbsp;|&nbsp;&nbsp;Actualización: {formatDate(project.updatedAt)}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center" style={{ fontSize: '1.5rem', color: 'var(--color-dark)' }}>
                                            <i className="bi bi-chevron-double-right"></i>
                                        </div>
                                    </div>
                                    {/* Progress bar in bottom right corner */}
                                    <div className="position-absolute" style={{ bottom: '1rem', right: '2.5rem' }}>
                                        <div className="progress" style={{ width: '120px', height: '6px' }}>
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{
                                                    width: `${(project.completedSections / project.totalSections) * 100}%`,
                                                    backgroundColor: 'var(--color-dark)'
                                                }}
                                                aria-valuenow={(project.completedSections / project.totalSections) * 100}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))
                    ) : (
                        <div className="create-submission-box text-center py-5" onClick={toggleProjectForm} style={{ cursor: 'pointer' }}>
                            <p style={{ fontWeight: 500, fontSize: '1.1rem' }}>BIENVENIDO</p>
                            <p>Todavía no hay proyectos en curso.</p>
                            <p>
                                <a
                                    onClick={toggleProjectForm}
                                    className="text-decoration-none"
                                    style={{ color: 'var(--color-dark)' }}
                                >
                                    Haz click <span>aquí</span> para comenzar un proyecto nuevo.
                                </a>
                            </p>
                        </div>
                    )}

                    {/* Create new project button at the bottom - only show when there are projects */}
                    {!isLoading && projects.length > 0 && (
                        <div
                            className="create-submission-box"
                            onClick={toggleProjectForm}
                            style={{ cursor: 'pointer' }}
                        >
                            +&nbsp;&nbsp;&nbsp;Nuevo proyecto
                        </div>
                    )}
                </div>

                {/* Project Form Sidebar */}
                <div className={`submission-form-sidebar ${isProjectFormOpen ? 'open' : ''}`}>
                    <button
                        className="close-form-btn"
                        onClick={toggleProjectForm}
                        aria-label="Cerrar formulario"
                    >
                        ×
                    </button>

                    <div className="form-content d-flex flex-column h-100">
                        <div className="flex-grow-1">
                            <h4>{formStep === 1 ? 'Información del Proyecto' : 'Información del Cliente'}</h4>

                            {formError && (
                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                    {formError}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setFormError('')}
                                    ></button>
                                </div>
                            )}

                            <div className="form-fields">
                                {formStep === 1 ? (
                                    <>
                                        {/* Step 1: Project Information */}
                                        <div className="mb-3">
                                            <label className="form-label d-flex align-items-center">
                                                Nombre interno del proyecto *
                                                <span
                                                    className="featured-icon ms-2"
                                                    onMouseEnter={(e) => {
                                                        const tooltip = document.createElement('div');
                                                        tooltip.className = 'custom-tooltip';
                                                        tooltip.textContent = 'El que te aparecerá a ti';
                                                        document.body.appendChild(tooltip);

                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                                        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

                                                        // Position tooltip above the icon, centered horizontally
                                                        tooltip.style.left = rect.left + scrollLeft + (rect.width / 2) - (tooltip.offsetWidth / 2) + 'px';
                                                        tooltip.style.top = rect.top + scrollTop - tooltip.offsetHeight - 8 + 'px';

                                                        e.currentTarget.setAttribute('data-tooltip-id', Date.now().toString());
                                                    }}
                                                    onMouseLeave={() => {
                                                        const tooltips = document.querySelectorAll('.custom-tooltip');
                                                        tooltips.forEach(tooltip => tooltip.remove());
                                                    }}
                                                >
                                                    <i className="bi bi-question-circle-fill"></i>
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ej: Casa García - Reforma cocina"
                                                value={projectFormData.internalName}
                                                onChange={(e) => handleInputChange('internalName', e.target.value)}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label d-flex align-items-center">
                                                Título del proyecto *
                                                <span
                                                    className="featured-icon ms-2"
                                                    onMouseEnter={(e) => {
                                                        const tooltip = document.createElement('div');
                                                        tooltip.className = 'custom-tooltip';
                                                        tooltip.textContent = 'El que le aparecerá al cliente';
                                                        document.body.appendChild(tooltip);

                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                                        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

                                                        // Position tooltip above the icon, centered horizontally
                                                        tooltip.style.left = rect.left + scrollLeft + (rect.width / 2) - (tooltip.offsetWidth / 2) + 'px';
                                                        tooltip.style.top = rect.top + scrollTop - tooltip.offsetHeight - 8 + 'px';

                                                        e.currentTarget.setAttribute('data-tooltip-id', Date.now().toString());
                                                    }}
                                                    onMouseLeave={() => {
                                                        const tooltips = document.querySelectorAll('.custom-tooltip');
                                                        tooltips.forEach(tooltip => tooltip.remove());
                                                    }}
                                                >
                                                    <i className="bi bi-question-circle-fill"></i>
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ej: Reforma integral de vivienda"
                                                value={projectFormData.title}
                                                onChange={(e) => handleInputChange('title', e.target.value)}
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label className="form-label">Tipo de Intervención *</label>
                                            <div className="d-flex gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${projectFormData.interventionType === 'NEW_CONSTRUCTION' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('interventionType', projectFormData.interventionType === 'NEW_CONSTRUCTION' ? '' : 'NEW_CONSTRUCTION')}
                                                >
                                                    Obra Nueva
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${projectFormData.interventionType === 'RENOVATION' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('interventionType', projectFormData.interventionType === 'RENOVATION' ? '' : 'RENOVATION')}
                                                >
                                                    Reforma
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="form-label">Superficie de Intervención *</label>
                                            <div className="d-flex gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${projectFormData.surfaceArea === 'AREA_LOWER_THAN_50' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('surfaceArea', projectFormData.surfaceArea === 'AREA_LOWER_THAN_50' ? '' : 'AREA_LOWER_THAN_50')}
                                                >
                                                    &lt; 50m²
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${projectFormData.surfaceArea === 'AREA_BETWEEN_51_AND_100' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('surfaceArea', projectFormData.surfaceArea === 'AREA_BETWEEN_51_AND_100' ? '' : 'AREA_BETWEEN_51_AND_100')}
                                                >
                                                    51 - 100m²
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${projectFormData.surfaceArea === 'AREA_BETWEEN_101_AND_200' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('surfaceArea', projectFormData.surfaceArea === 'AREA_BETWEEN_101_AND_200' ? '' : 'AREA_BETWEEN_101_AND_200')}
                                                >
                                                    101 - 200m²
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${projectFormData.surfaceArea === 'AREA_BETWEEN_201_AND_500' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('surfaceArea', projectFormData.surfaceArea === 'AREA_BETWEEN_201_AND_500' ? '' : 'AREA_BETWEEN_201_AND_500')}
                                                >
                                                    201 - 500m²
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${projectFormData.surfaceArea === 'AREA_GREATER_THAN_500' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('surfaceArea', projectFormData.surfaceArea === 'AREA_GREATER_THAN_500' ? '' : 'AREA_GREATER_THAN_500')}
                                                >
                                                    &gt; 500m²
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* Step 2: Client Information */}
                                        <div className="mb-3">
                                            <label className="form-label">Correo electrónico del cliente *</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                placeholder="cliente@ejemplo.com"
                                                value={projectFormData.clientEmail}
                                                onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label className="form-label">El cliente es *</label>
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className={`btn ${projectFormData.clientSize === 'SINGULAR' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('clientSize', 'SINGULAR')}
                                                >
                                                    Una personal sola
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn ${projectFormData.clientSize === 'PLURAL' ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => handleInputChange('clientSize', 'PLURAL')}
                                                >
                                                    Más de una persona
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Bottom buttons */}
                        <div className="border-top pt-3 mt-4">
                            {formStep === 1 ? (
                                <div className="d-flex justify-content-between gap-2">
                                    <button type="button" className="btn btn-cancel-custom" onClick={toggleProjectForm}>
                                        Cancelar
                                    </button>
                                    <button type="button" className="btn btn-create-custom" onClick={(e) => { e.preventDefault(); handleNextStep(); }}>
                                        Siguiente
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmitProject}>
                                    <div className="d-flex justify-content-between gap-2">
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-cancel-custom" onClick={toggleProjectForm}>
                                                Cancelar
                                            </button>
                                            <button type="button" className="btn btn-outline-secondary" onClick={handlePreviousStep}>
                                                Anterior
                                            </button>
                                        </div>
                                        <button type="submit" className="btn btn-create-custom" disabled={isSubmitting}>
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Creando...
                                                </>
                                            ) : (
                                                'Crear Proyecto'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>

                {isProjectFormOpen && <div className="form-overlay" onClick={toggleProjectForm}></div>}
            </div>
        </div>
    );
};

export default ArchitectDashboard;